import { useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import dayjs from 'dayjs';
import type { WorklogFormProps } from './useWorklogSubmit';
import {
  type WorklogModalMode,
  WorklogModalMode as WorklogModalModeOptions,
} from '@/features/worklogModalSlice';

interface UseWorklogAnalyticsParams {
  mode: WorklogModalMode;
}

/**
 * Custom hook to handle Google Analytics tracking for worklog events.
 */
export const useWorklogAnalytics = ({ mode }: UseWorklogAnalyticsParams) => {
  const trackEvent = useCallback((event: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event,
      },
    });
  }, []);

  const trackWorklogEvent = useCallback(
    (
      values: WorklogFormProps,
      originalData?: { status?: string | null; date?: string }
    ) => {
      const handleBulkWorklogChange = (values: WorklogFormProps) => {
        const { status } = values;
        // A bulk add that records no work reports nothing.
        if (!status || status === WorkLogStatus.NOT_ADDED) {
          return;
        }
        trackEvent(GoogleEvents.worklogActivity.bulkAdd[status]);
      };

      const handleSingleWorklogChange = (
        values: WorklogFormProps,
        originalData: { status?: string | null; date?: string }
      ) => {
        const hasStatusChanged = values.status !== originalData?.status;
        const hasDateChanged =
          originalData?.date &&
          values.date.getTime() !==
            dayjs.utc(originalData.date).toDate().getTime();

        let worklogStatus: string;
        switch (values.status) {
          case WorkLogStatus.COMPLETED:
            worklogStatus = GoogleEvents.worklogStatusWorkCompleted;
            break;
          case WorkLogStatus.WORK_NEEDED:
            worklogStatus = GoogleEvents.worklogStatusWorkNeeded;
            break;
          default:
            worklogStatus = GoogleEvents.worklogStatusNoWorkNeeded;
        }

        const worklogMode =
          mode === WorklogModalModeOptions.EDIT
            ? GoogleEvents.worklogStatusEdited
            : GoogleEvents.worklogStatusAdded;

        trackEvent(worklogStatus);

        if (hasStatusChanged) {
          trackEvent(worklogMode);
        }

        if (values.notes !== '') {
          trackEvent(GoogleEvents.worklogNotesWithContent);
        }

        if (hasDateChanged) {
          trackEvent(GoogleEvents.worklogDateFieldChanged);
        }
      };

      if (mode === WorklogModalModeOptions.BULK_ADD) {
        handleBulkWorklogChange(values);
      } else {
        handleSingleWorklogChange(values, originalData ?? {});
      }
    },
    [mode, trackEvent]
  );

  return {
    trackWorklogEvent,
  };
};
